import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import prefersReducedMotion from 'utils/prefersReducedMotion';

export const FLIP_DURATION_MS = 250;
export const FLIP_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
// Below this distance a move is rounding noise, not a visible jump.
const MIN_DELTA_PX = 0.5;

const hasSize = (rect) => rect.width > 0 && rect.height > 0;

const isInViewport = (rect, viewport) =>
  rect.bottom > 0 &&
  rect.right > 0 &&
  rect.top < viewport.height &&
  rect.left < viewport.width;

/** The same box, its top-left corner put at `point` (viewport coordinates). */
export const placeRectAt = (rect, point) => ({
  left: point.left,
  top: point.top,
  right: point.left + rect.width,
  bottom: point.top + rect.height,
  width: rect.width,
  height: rect.height,
});

/**
 * Compares two snapshots (key -> rect) and lists the moves to play, as the
 * offset from the new place back to the old one. Skipped: items new or gone,
 * items with no size at either end (collapsed, hidden), items off screen at
 * both ends, items that did not move.
 */
export const computeFlipMoves = (before, after, viewport) => {
  const moves = [];
  after.forEach((last, key) => {
    const first = before.get(key);
    if (!first || !hasSize(first) || !hasSize(last)) return;
    if (!isInViewport(first, viewport) && !isInViewport(last, viewport)) return;
    const dx = first.left - last.left;
    const dy = first.top - last.top;
    if (Math.abs(dx) < MIN_DELTA_PX && Math.abs(dy) < MIN_DELTA_PX) return;
    moves.push({ key, dx, dy, first, last });
  });
  return moves;
};

const flipKeyframes = (dx, dy) => [
  { translate: `${dx}px ${dy}px` },
  { translate: '0px 0px' },
];

// A card that changes list is clipped by its new list (overflow auto) on the
// way there, so it would appear to slide in from the list's edge. It flies as
// a fixed copy over the page instead, the real card hidden until it lands.
const createOverlay = (element, rect, keyAttribute) => {
  const overlay = element.cloneNode(true);
  overlay.removeAttribute(keyAttribute);
  overlay.setAttribute('aria-hidden', 'true');
  // The copy leaves the list it inherited its text style from.
  const computed = window.getComputedStyle(element);
  Object.assign(overlay.style, {
    position: 'fixed',
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    margin: '0',
    zIndex: '40',
    pointerEvents: 'none',
    fontFamily: computed.fontFamily,
    fontSize: computed.fontSize,
    lineHeight: computed.lineHeight,
    color: computed.color,
  });
  document.body.appendChild(overlay);
  return overlay;
};

/**
 * Animates the items of a container from where they were to where a state
 * update put them (FLIP).
 *
 * Call `capture(origins)` right before the update: it records where each item
 * inside `containerRef` that carries `keyAttribute` is on screen, mid-flight
 * position included. `origins` (key -> { left, top }) overrides the start of
 * some items: a dropped card starts where the user let go of it. The next
 * render plays the moves, cancelling those still running. Nothing under
 * reduced motion, nor where the Web Animations API is missing.
 */
const useFlipLayout = (
  containerRef,
  { keyAttribute = 'data-flip-id' } = {},
) => {
  const snapshotRef = useRef(null);
  const runningRef = useRef(new Map());

  const stopAll = useCallback(() => {
    const running = runningRef.current;
    running.forEach(({ animation, land }) => {
      animation.cancel();
      // Not left to the cancel event: it comes a frame later, after the next
      // move may have hidden the same card again.
      land?.();
    });
    running.clear();
  }, []);

  const capture = useCallback(
    (origins = {}) => {
      const container = containerRef.current;
      if (!container || prefersReducedMotion()) {
        snapshotRef.current = null;
        return;
      }
      const before = new Map();
      const parents = new Map();
      container.querySelectorAll(`[${keyAttribute}]`).forEach((element) => {
        const key = element.getAttribute(keyAttribute);
        const overlay = runningRef.current.get(key)?.overlay;
        before.set(key, (overlay || element).getBoundingClientRect());
        // A card still flying as a copy is not yet inside its list: it keeps
        // flying as a copy, or its list would clip it.
        parents.set(key, overlay ? null : element.parentElement);
      });
      Object.entries(origins).forEach(([key, point]) => {
        const rect = before.get(key);
        if (rect && point) before.set(key, placeRectAt(rect, point));
      });
      snapshotRef.current = { before, parents };
    },
    [containerRef, keyAttribute],
  );

  // No dependency list: the snapshot is consumed by whichever render follows
  // the capture.
  useLayoutEffect(() => {
    const snapshot = snapshotRef.current;
    const container = containerRef.current;
    if (!snapshot || !container) return;
    const { before, parents } = snapshot;
    snapshotRef.current = null;
    // Cancelled first, so the new places are measured without any offset.
    stopAll();

    const elements = new Map();
    const after = new Map();
    container.querySelectorAll(`[${keyAttribute}]`).forEach((element) => {
      if (typeof element.animate !== 'function') return;
      const key = element.getAttribute(keyAttribute);
      elements.set(key, element);
      after.set(key, element.getBoundingClientRect());
    });

    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
    computeFlipMoves(before, after, viewport).forEach(
      ({ key, dx, dy, last }) => {
        const element = elements.get(key);
        const options = { duration: FLIP_DURATION_MS, easing: FLIP_EASING };

        if (element.parentElement === parents.get(key)) {
          const animation = element.animate(flipKeyframes(dx, dy), options);
          const entry = { animation };
          runningRef.current.set(key, entry);
          animation.onfinish = () => {
            if (runningRef.current.get(key) === entry) {
              runningRef.current.delete(key);
            }
          };
          return;
        }

        const overlay = createOverlay(element, last, keyAttribute);
        const previousVisibility = element.style.visibility;
        element.style.visibility = 'hidden';
        const animation = overlay.animate(flipKeyframes(dx, dy), options);
        let landed = false;
        const entry = { animation, overlay };
        entry.land = () => {
          if (landed) return;
          landed = true;
          overlay.remove();
          element.style.visibility = previousVisibility;
          if (runningRef.current.get(key) === entry) {
            runningRef.current.delete(key);
          }
        };
        runningRef.current.set(key, entry);
        animation.onfinish = entry.land;
      },
    );
  });

  useEffect(() => stopAll, [stopAll]);

  return { capture };
};

export default useFlipLayout;
